import {
  deriveTranslationStatus,
  type ContentTranslationUiStatus,
  type TranslationStatusRow,
} from '@duke-hq/shared-constants/contentTranslationStatus';

// The worklist's grouping: every (resource, language) cell whose status needs a
// look, grouped by the shared attention ladder and merged per resource, so a
// resource failing in seven languages is one row with seven tags, not seven rows.

export type ExceptionGroupKey = 'failed' | 'stale' | 'inFlight' | 'gaps';

// Ladder order, matching coverageStatus: failed > stale > in-flight > gaps.
const EXCEPTION_GROUP_ORDER: readonly ExceptionGroupKey[] = ['failed', 'stale', 'inFlight', 'gaps'];

export type ExceptionCell = {
  language: string;
  status: ContentTranslationUiStatus;
  untranslatedSegmentCount: number | null;
  totalSegmentCount: number | null;
};

export type ExceptionRow = {
  resourceId: string;
  resourceTitle: string;
  unitOfCompetencyId: string | null;
  unitTitle: string | null;
  resourceKind: 'PAGE' | 'CONVERSATION';
  currentPublishedVersionId: string | null;
  cells: ExceptionCell[];
};

export type ExceptionGroups = Record<ExceptionGroupKey, ExceptionRow[]>;

type RowLike = TranslationStatusRow & {
  language: string;
  untranslatedSegmentCount?: number | null;
  totalSegmentCount?: number | null;
};

type ResourceLike = {
  resourceId: string;
  title: string;
  resourceKind: 'PAGE' | 'CONVERSATION';
  currentPublishedVersionId: string | null;
  rows: RowLike[];
};

type UnitLike = {
  unitOfCompetencyId: string | null;
  unitTitle: string | null;
  resources: ResourceLike[];
};

function groupKeyFor(status: ContentTranslationUiStatus): ExceptionGroupKey | null {
  switch (status) {
    case 'failed':
      return 'failed';
    case 'stale':
      return 'stale';
    case 'queued':
    case 'generating':
      return 'inFlight';
    case 'available-partial':
      return 'gaps';
    case 'available':
    case 'not-translated':
    // Deliberately stopped, not a problem to triage.
    case 'cancelled':
      return null;
  }
}

export function buildExceptionGroups(
  units: readonly UnitLike[],
  supportedLanguages: readonly string[],
): ExceptionGroups {
  const groups: ExceptionGroups = { failed: [], stale: [], inFlight: [], gaps: [] };
  // One merged row per (group, resource), keeping discovery order.
  const rowIndex = new Map<string, ExceptionRow>();

  for (const unit of units) {
    for (const resource of unit.resources) {
      if (resource.currentPublishedVersionId === null) {
        continue;
      }
      const rowByLanguage = new Map(resource.rows.map((row) => [row.language, row]));
      for (const language of supportedLanguages) {
        const row = rowByLanguage.get(language);
        const status = deriveTranslationStatus(row, resource.currentPublishedVersionId);
        const key = groupKeyFor(status);
        if (key === null) {
          continue;
        }
        const mergeKey = `${key}:${resource.resourceId}`;
        let merged = rowIndex.get(mergeKey);
        if (merged === undefined) {
          merged = {
            resourceId: resource.resourceId,
            resourceTitle: resource.title,
            unitOfCompetencyId: unit.unitOfCompetencyId,
            unitTitle: unit.unitTitle,
            resourceKind: resource.resourceKind,
            currentPublishedVersionId: resource.currentPublishedVersionId,
            cells: [],
          };
          rowIndex.set(mergeKey, merged);
          groups[key].push(merged);
        }
        merged.cells.push({
          language,
          status,
          untranslatedSegmentCount: row?.untranslatedSegmentCount ?? null,
          totalSegmentCount: row?.totalSegmentCount ?? null,
        });
      }
    }
  }

  return groups;
}

export function totalCells(rows: readonly ExceptionRow[]): number {
  return rows.reduce((sum, row) => sum + row.cells.length, 0);
}

export function hasExceptions(groups: ExceptionGroups): boolean {
  return EXCEPTION_GROUP_ORDER.some((key) => groups[key].length > 0);
}
